import { useHistory, useParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Link } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Estate_FieldsFragment, useEstate_GetEstatesByPortalIdQuery } from 'queries';

import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  borderRadius: 8,
  backgroundColor: '#DCF4FE',
  color: '#1C2541',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(() => ({
  borderRadius: 8,
  height: 30,
  '.MuiAccordionSummary-content': {
    margin: 0,
    height: 30,
  },
  '.MuiAccordionSummary-expandIconWrapper': {
    color: '#1C2541',
  },
  '&:hover': {
    backgroundColor: '#D351E1',
    color: '#FFF',
    '.MuiAccordionSummary-expandIconWrapper': {
      color: '#FFF',
    },
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const useStyles = makeStyles((theme) => ({
  accordionSummary: {
    display: 'flex',
    alignItems: 'center',
  },
  navLink: {
    width: '100%',
    padding: theme.spacing(1, 2),
    color: '#1C2541',
    cursor: 'pointer',
    display: 'flex',
    '&:hover': {
      backgroundColor: '#D351E1',
      color: '#FFF',
      borderRadius: 8,
    },
  },
  navLinkTitle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  navLinkActive: {
    backgroundColor: 'white',
    color: '#1C2541',
    borderRadius: 8,
  },
}));

type Props = {
  type: 'estates' | 'vaults';
  isExpanded: boolean;
  toggleExpanded: () => void;
};

const SubNavBar = ({ type, toggleExpanded, isExpanded }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId, estateId, folder } = useParams<Record<string, string>>();
  const limitOfEstatesToFetch = 5;
  const maxRecentlyUpdatedEstatesToShow = 4;

  const { data: estatesData } = useEstate_GetEstatesByPortalIdQuery({
    variables: { input: { portalId, isArchived: false, limit: limitOfEstatesToFetch } },
  });
  const estates = estatesData?.data || [];
  const recentlyUpdatedEstates = estates.slice(0, maxRecentlyUpdatedEstatesToShow);

  const handleItemClick = (estate: Estate_FieldsFragment) => {
    if (type === 'estates') {
      history.push(
        `/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
      );
      history.go(0); // Force the page to re-render.
      return;
    }

    history.push(
      `/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.VAULT}`,
    );
  };

  const handleMoreClick = () => {
    if (type === 'estates') {
      history.push(`/home/${portalId}/home`);
      return;
    }

    history.push(`/home/${portalId}/vault`);
  };

  const getIsActive = (estate: Estate_FieldsFragment) => {
    if (type === 'estates') {
      return estate.id === estateId;
    }

    return estate.documentsFolderId === folder;
  };

  return (
    <Accordion key="type" expanded={isExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={toggleExpanded}>
        <div className={classes.accordionSummary}>
          {type === 'estates' ? (
            <WorkOutlineOutlinedIcon
              sx={{
                marginRight: '10px',
              }}
              fontSize="small"
            />
          ) : (
            <SecurityIcon
              sx={{
                marginRight: '10px',
              }}
              fontSize="small"
            />
          )}
          {type === 'estates' ? 'My Projects' : ' My Vaults'}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        {recentlyUpdatedEstates.map((estate) => (
          <Link
            component="div"
            variant="body2"
            underline="none"
            key={estate.id}
            className={clsx(classes.navLink, getIsActive(estate) && classes.navLinkActive)}
            onClick={() => handleItemClick(estate)}
          >
            <div className={classes.navLinkTitle}>
              {estate.estateName} {type === 'vaults' ? 'vault' : ''}
            </div>
          </Link>
        ))}
        {estates && estates.length > maxRecentlyUpdatedEstatesToShow && (
          <Link
            component="button"
            variant="body2"
            underline="none"
            key="More"
            className={classes.navLink}
            onClick={handleMoreClick}
          >
            More...
          </Link>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default SubNavBar;
